import { useEffect, useState } from 'react';
import type { IconPackId, UserUiSettings } from '@ukrop-nm/shared';
import {
  ACCOUNT_CHANGED_EVENT,
  SETTINGS_CHANGED_EVENT,
  accountStorageKey,
  emitSettingsChanged,
  getAccountOwner,
} from './accountScope';
import { normalizeDiagramOpenZoom } from '@ukrop-nm/shared';

const DEFAULTS: UserUiSettings = {
  theme: 'light',
  iconPack: 'ukrop',
  displayName: '',
  rememberLogin: true,
  diagramOpenZoom: 50,
};

const ICON_PACKS: IconPackId[] = ['ukrop', 'classic_network', 'flat_cloud', 'minimal', 'outline'];

function isIconPack(value: unknown): value is IconPackId {
  return typeof value === 'string' && (ICON_PACKS as string[]).includes(value);
}

function readJson(key: string): Partial<UserUiSettings> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<UserUiSettings>;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function guestFallback(): Partial<UserUiSettings> {
  const out: Partial<UserUiSettings> = {};
  try {
    const theme = localStorage.getItem('ukrop-nm.theme');
    if (theme === 'light' || theme === 'dark') out.theme = theme;
    const pack = localStorage.getItem('ukrop-nm.iconPack');
    if (isIconPack(pack)) out.iconPack = pack;
  } catch {
    /* ignore */
  }
  return out;
}

export function loadUserUiSettings(): UserUiSettings {
  const stored = readJson(accountStorageKey('ui-settings')) ?? {};
  const guest = guestFallback();
  const theme = stored.theme === 'dark' || stored.theme === 'light' ? stored.theme : guest.theme;
  const iconPack = isIconPack(stored.iconPack) ? stored.iconPack : guest.iconPack;
  return {
    theme: theme ?? DEFAULTS.theme,
    iconPack: iconPack ?? DEFAULTS.iconPack,
    displayName: typeof stored.displayName === 'string' ? stored.displayName : DEFAULTS.displayName,
    rememberLogin: typeof stored.rememberLogin === 'boolean' ? stored.rememberLogin : DEFAULTS.rememberLogin,
    diagramOpenZoom: normalizeDiagramOpenZoom(stored.diagramOpenZoom ?? DEFAULTS.diagramOpenZoom),
  };
}

export function saveUserUiSettings(patch: Partial<UserUiSettings>): UserUiSettings {
  const next = { ...loadUserUiSettings(), ...patch };
  try {
    localStorage.setItem(accountStorageKey('ui-settings'), JSON.stringify(next));
    if (!getAccountOwner()) {
      localStorage.setItem('ukrop-nm.theme', next.theme);
      localStorage.setItem('ukrop-nm.iconPack', next.iconPack);
    }
  } catch {
    /* ignore */
  }
  emitSettingsChanged();
  return next;
}

export function applyUserUiSettings(settings: UserUiSettings | null | undefined) {
  if (!settings || typeof settings !== 'object') return loadUserUiSettings();
  return saveUserUiSettings(settings);
}

export function useUserUiSettings() {
  const [settings, setSettings] = useState(loadUserUiSettings);
  useEffect(() => {
    const refresh = () => setSettings(loadUserUiSettings());
    window.addEventListener(ACCOUNT_CHANGED_EVENT, refresh);
    window.addEventListener(SETTINGS_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener(ACCOUNT_CHANGED_EVENT, refresh);
      window.removeEventListener(SETTINGS_CHANGED_EVENT, refresh);
    };
  }, []);
  return {
    settings,
    update: (patch: Partial<UserUiSettings>) => setSettings(saveUserUiSettings(patch)),
  };
}
